import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { socketService } from "services/socketService";
import useAuth from "hooks/useAuth";
import { canAccessAdminPanel } from "utils/adminAccess";
import { getHlsJobs } from "api/admin/hlsJobs";
import router from "routes";

// Global HLS conversion queue state — mounted once at the app root (see
// App.jsx) so the queue/progress panel survives route navigation. Admin-only:
// the backend endpoint and WS channel it reads from are both admin-gated.
// Live updates arrive via ws/admin-notifications/ (hls_job_update, pushed
// from modules/video_utils.py::_push_hls_job_update) and stay live everywhere
// so the persistent panel keeps tracking a conversion even if the admin
// navigates away from /admin. The REST poll (see api/admin/hlsJobs.js) is
// only the reconnect-safe fallback/initial load — it's restricted to /admin
// pages so the request isn't fired app-wide every 5s for every admin session.
const HlsJobsContext = createContext();

const ACTIVE_STATUSES = ["queued", "processing"];
const POLL_INTERVAL_MS = 5000;

export const HlsJobsProvider = ({ children }) => {
  const { user, isLoggedIn } = useAuth();
  const isAdmin = isLoggedIn && canAccessAdminPanel(user);
  const [jobs, setJobs] = useState([]);
  const [isOnAdminRoute, setIsOnAdminRoute] = useState(() =>
    router.state.location.pathname.startsWith("/admin")
  );

  const upsertJob = useCallback((job) => {
    if (!job?.id) return;
    setJobs((prev) => {
      const idx = prev.findIndex((j) => j.id === job.id);
      if (idx === -1) return [job, ...prev];
      const next = [...prev];
      next[idx] = { ...next[idx], ...job };
      return next;
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const list = await getHlsJobs();
      setJobs(list);
    } catch {
      // Silent — panel just keeps showing its last known state.
    }
  }, []);

  // Track the current route without needing to sit inside <RouterProvider>'s
  // tree (this provider wraps it in App.jsx) — the data router exposes a
  // subscribe API for exactly this.
  useEffect(() => {
    const unsubscribe = router.subscribe((state) => {
      setIsOnAdminRoute(state.location.pathname.startsWith("/admin"));
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setJobs([]);
      return undefined;
    }

    refresh();

    const onUpdate = (data) => {
      if (data?.job) upsertJob(data.job);
    };
    socketService.on("hls_job_update", onUpdate);

    return () => {
      socketService.off("hls_job_update", onUpdate);
    };
  }, [isAdmin, refresh, upsertJob]);

  useEffect(() => {
    if (!isAdmin || !isOnAdminRoute) return undefined;

    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isAdmin, isOnAdminRoute, refresh]);

  const activeJobs = jobs
    .filter((j) => ACTIVE_STATUSES.includes(j.status))
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const recentJobs = jobs
    .filter((j) => !ACTIVE_STATUSES.includes(j.status))
    .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));

  const value = { jobs, activeJobs, recentJobs, refresh };

  return (
    <HlsJobsContext.Provider value={value}>{children}</HlsJobsContext.Provider>
  );
};

export const useHlsJobs = () => {
  const ctx = useContext(HlsJobsContext);
  if (!ctx) throw new Error("useHlsJobs must be used inside HlsJobsProvider");
  return ctx;
};

export default HlsJobsContext;
